import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { clienteConfigService } from '../../../services/planTrabajoService';
import { supabase } from '../../../lib/supabase';
import TrabajoForm from './TrabajoForm';

const AddTrabajoModal = ({ isOpen, onClose, onConfirm, preselectedDate, weekDates = [] }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState('cliente'); // 'cliente' | 'confirm'
  const [clientes, setClientes] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [productos, setProductos] = useState([]);
  const [empleados, setEmpleados] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [guardarHabitual, setGuardarHabitual] = useState(false);
  const [showMasOpciones, setShowMasOpciones] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [validationError, setValidationError] = useState('');

  // Formulario unificado (mismos campos que EditTrabajoModal)
  const [form, setForm] = useState({
    fecha: '',
    cantidad_piezas: 1,
    producto_id: '',
    empleado_id: '',
    prioridad: 'NORMAL',
    tipo_corte: 'NORMAL',
    formato_corte: 'ENTERO',
    quiere_huesos: false,
    guardar_merma: false,
    observaciones: '',
  });

  // Cliente sin producto habitual → mostrar selector obligatorio
  const needsProducto = !!(selectedCliente && !selectedConfig?.producto_id);

  useEffect(() => {
    if (isOpen) {
      setStep('cliente');
      setSelectedCliente(null);
      setSelectedConfig(null);
      setGuardarHabitual(false);
      setShowMasOpciones(false);
      setValidationError('');
      resetForm(preselectedDate || '');
      loadData();
    }
  }, [isOpen, preselectedDate]);

  const resetForm = (fecha = '') => {
    setForm({
      fecha,
      cantidad_piezas: 1,
      producto_id: '',
      empleado_id: '',
      prioridad: 'NORMAL',
      tipo_corte: 'NORMAL',
      formato_corte: 'ENTERO',
      quiere_huesos: false,
      guardar_merma: false,
      observaciones: '',
    });
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [clientesResult, configsData, productosResult, empsResult] = await Promise.all([
        supabase?.from('clientes')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
        clienteConfigService?.getAll(),
        supabase?.from('productos')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
        supabase?.from('empleados')?.select('id, nombre')?.eq('activo', true)?.order('nombre'),
      ]);
      setClientes(clientesResult?.data || []);
      setConfigs(configsData || []);
      setProductos(productosResult?.data || []);
      setEmpleados(empsResult?.data || []);
    } catch (err) {
      console.error('[AddTrabajoModal.loadData]', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectCliente = (cliente) => {
    const config = configs?.find(c => c?.cliente_id === cliente?.id);
    setSelectedCliente(cliente);
    setSelectedConfig(config || null);
    setGuardarHabitual(false);
    setValidationError('');
    setShowMasOpciones(false);

    // Pre-seleccionar día habitual si hay config y no hay fecha preseleccionada
    let fechaInicial = preselectedDate || '';
    if (!fechaInicial && config?.dia_semana && weekDates?.length >= config?.dia_semana) {
      fechaInicial = weekDates?.[config?.dia_semana - 1] || '';
    }

    // Heredar valores de la config habitual
    setForm({
      fecha: fechaInicial,
      cantidad_piezas: config?.cantidad_habitual || 1,
      producto_id: '',  // vacío: se usa config?.producto_id como habitual; override aquí
      empleado_id: config?.empleado_preferente_id || '',
      prioridad: config?.prioridad_habitual || 'NORMAL',
      tipo_corte: config?.tipo_corte || 'NORMAL',
      formato_corte: config?.formato_corte || 'ENTERO',
      quiere_huesos: config?.quiere_huesos || false,
      guardar_merma: config?.guardar_merma || false,
      observaciones: config?.observaciones_habituales || config?.observaciones || '',
    });

    setStep('confirm');
  };

  const handleChange = (key, value) => {
    setForm(prev => ({ ...prev, [key]: value }));
    setValidationError('');
  };

  const handleGoToConfig = () => {
    onClose();
    navigate(`/clientes/${selectedCliente?.id}?tab=condiciones_corte`);
  };

  const handleConfirm = async () => {
    if (saving) return;
    setValidationError('');

    if (!selectedCliente?.id) {
      setValidationError('Selecciona un cliente');
      return;
    }

    // producto_id final: override en form (si se cambió en Más opciones o needsProducto),
    // o el habitual de la config
    const productoIdFinal = form?.producto_id || selectedConfig?.producto_id || null;

    if (!productoIdFinal) {
      setValidationError('Selecciona un producto');
      return;
    }

    if (!form?.fecha) {
      setValidationError('Selecciona un día');
      return;
    }

    if ((form?.cantidad_piezas || 1) < 1) {
      setValidationError('La cantidad debe ser al menos 1');
      return;
    }

    setSaving(true);
    try {
      // Guardar producto como habitual si se marcó
      if (guardarHabitual && form?.producto_id) {
        try {
          if (selectedConfig) {
            await clienteConfigService?.upsert({ ...selectedConfig, producto_id: form?.producto_id });
          } else {
            await clienteConfigService?.upsert({
              cliente_id: selectedCliente?.id,
              producto_id: form?.producto_id,
              cantidad_habitual: form?.cantidad_piezas,
              dia_semana: form?.fecha ? (new Date(form?.fecha + 'T12:00:00')?.getDay() || 7) : null,
              activo: true,
            });
          }
        } catch (configErr) {
          console.error('[AddTrabajoModal.guardarHabitual]', configErr);
        }
      }

      // Construir trabajo con todos los campos del formulario
      let trabajoData;
      if (selectedConfig) {
        trabajoData = clienteConfigService?.buildTrabajoFromConfig(selectedConfig, {
          cantidad_piezas: form?.cantidad_piezas,
          fecha: form?.fecha,
          producto_id: productoIdFinal,
          empleado_id: form?.empleado_id || undefined,
          prioridad: form?.prioridad,
          tipo_corte: form?.tipo_corte,
          formato_corte: form?.formato_corte,
          quiere_huesos: form?.quiere_huesos,
          guardar_merma: form?.guardar_merma,
          observaciones: form?.observaciones || undefined,
        });
      } else {
        trabajoData = {
          cliente_id: selectedCliente?.id,
          producto_id: productoIdFinal,
          cantidad_piezas: form?.cantidad_piezas,
          fecha: form?.fecha,
          estado: 'PENDIENTE',
          tipo_trabajo: 'PUNTUAL',
          empleado_id: form?.empleado_id || undefined,
          prioridad: form?.prioridad,
          tipo_corte: form?.tipo_corte,
          formato_corte: form?.formato_corte,
          quiere_huesos: form?.quiere_huesos,
          guardar_merma: form?.guardar_merma,
          observaciones: form?.observaciones || undefined,
        };
      }

      if (!trabajoData?.producto_id) {
        setValidationError('Selecciona un producto');
        setSaving(false);
        return;
      }

      await onConfirm(trabajoData);
      onClose();
    } catch (err) {
      console.error('[AddTrabajoModal.handleConfirm]', err);
      setValidationError(err?.message || 'Error al crear el trabajo');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)' }}>
      <div className="w-full max-w-lg rounded-2xl border border-white/10 overflow-hidden max-h-[90vh] flex flex-col" style={{ background: '#111111' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8 flex-shrink-0">
          <div>
            <h2 className="font-oranienbaum font-bold text-white text-xl">
              {step === 'cliente' ? '+ Añadir Trabajo' : selectedCliente?.nombre}
            </h2>
            {step === 'confirm' && selectedConfig && !needsProducto && (
              <p className="text-[#D6EF4E]/60 text-xs font-oranienbaum mt-0.5">✓ Configuración habitual cargada</p>
            )}
            {step === 'confirm' && !selectedConfig && (
              <p className="text-amber-400/70 text-xs font-oranienbaum mt-0.5">Sin configuración habitual</p>
            )}
            {step === 'confirm' && needsProducto && selectedConfig && (
              <p className="text-amber-400/70 text-xs font-oranienbaum mt-0.5">Selecciona un producto para continuar</p>
            )}
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white/70 transition-colors">
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-white/10 border-t-[#D6EF4E] rounded-full animate-spin" />
            </div>
          ) : step === 'cliente' ? (
            /* Step 1: Seleccionar cliente */
            <div className="grid grid-cols-2 gap-2.5">
              {clientes?.map(cliente => {
                const config = configs?.find(c => c?.cliente_id === cliente?.id);
                const color = config?.color;
                return (
                  <button
                    key={cliente?.id}
                    onClick={() => handleSelectCliente(cliente)}
                    className="p-4 rounded-xl border text-left transition-all hover:scale-[1.02] active:scale-[0.98] relative overflow-hidden"
                    style={color ? {
                      borderColor: `${color}50`,
                      background: `linear-gradient(135deg, ${color}18 0%, ${color}08 100%)`,
                      borderLeftWidth: '4px',
                      borderLeftColor: color,
                    } : {
                      borderColor: 'rgba(255,255,255,0.10)',
                      background: 'rgba(255,255,255,0.03)',
                    }}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {color ? (
                        <div className="w-3 h-3 rounded-full flex-shrink-0 shadow-sm" style={{ background: color }} />
                      ) : (
                        <div className="w-3 h-3 rounded-full flex-shrink-0 bg-white/15" />
                      )}
                      <div className="font-oranienbaum font-bold text-white text-sm leading-tight truncate">{cliente?.nombre}</div>
                    </div>
                    {config ? (
                      <div className="text-white/40 text-[10px] font-oranienbaum">
                        {config?.cantidad_habitual} piezas · {config?.recurrente ? 'Recurrente' : 'Puntual'}
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-amber-400/60 text-[10px] font-oranienbaum">Sin configuración</span>
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          ) : (
            /* Step 2: Formulario unificado */
            <div className="space-y-4">
              {/* Banner: cliente sin configuración habitual */}
              {!selectedConfig && (
                <div className="p-3 rounded-xl border border-amber-400/20 bg-amber-400/8 flex items-start gap-3">
                  <Icon name="AlertCircle" size={16} color="#FBBF24" className="flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <p className="font-oranienbaum text-amber-300 text-xs font-semibold">Sin configuración habitual</p>
                    <p className="font-oranienbaum text-amber-400/60 text-[10px] mt-0.5">
                      Puedes crear este trabajo puntual o configurar las condiciones habituales del cliente.
                    </p>
                  </div>
                  <button
                    onClick={handleGoToConfig}
                    className="flex-shrink-0 text-[10px] px-2.5 py-1.5 rounded-lg border border-amber-400/30 text-amber-300 hover:bg-amber-400/15 transition-colors font-oranienbaum whitespace-nowrap"
                  >
                    ⚙ Configurar cliente
                  </button>
                </div>
              )}

              <TrabajoForm
                mode="create"
                form={form}
                onChange={handleChange}
                productos={productos}
                empleados={empleados}
                weekDates={weekDates}
                selectedConfig={selectedConfig}
                needsProducto={needsProducto}
                guardarHabitual={guardarHabitual}
                onGuardarHabitual={setGuardarHabitual}
                clienteNombre={selectedCliente?.nombre || ''}
                validationError={validationError}
                showMasOpciones={showMasOpciones}
                onToggleMasOpciones={() => setShowMasOpciones(v => !v)}
              />

              {/* Acciones */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setStep('cliente'); setValidationError(''); }}
                  className="flex-1 py-3 rounded-xl border border-white/10 text-white/60 font-oranienbaum hover:bg-white/5 transition-colors"
                >
                  ← Volver
                </button>
                <button
                  onClick={handleConfirm}
                  disabled={saving || (needsProducto && !form?.producto_id)}
                  className="flex-2 px-6 py-3 rounded-xl font-oranienbaum font-bold text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ background: '#D6EF4E', flexGrow: 2 }}
                >
                  {saving ? 'Guardando...' : 'CONFIRMAR'}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddTrabajoModal;
